import _ from 'underscore';
import $ from 'jquery';
import { DeterministicWrapperView } from '@bingads-webui/deterministic-wrapper-view';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { pgridFactory } from '@bingads-webui-campaign/pgrid-factory';
import { registerCountryListEvent } from '@bingads-webui-campaign/delivery-status-popup-factory';
import { updateGridQuery } from '@bingads-webui-campaign/summary-grid-helper';
import { getReasonTemplates } from './show-rejection-reasons';

const pageContext = globalContext.require('PageContext');
const { handleSortChange } = globalContext.require('campaignui/feature/bingadsgrid/service/state-manager-helper');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/ads-tab');
const AppealAggregator = globalContext.requireDefault('campaignui/feature/editorial-appeal/aggregator');

export class AssetsGrid extends DeterministicWrapperView {
  viewFactory({
    router,
    scope,
    filterService,
    preferenceConfig,
    getGridConfig,
    getPlugins,
    setupDataSourceErrorHandling,
    isForVNext = false,
  }) {
    setupDataSourceErrorHandling(this);

    const gridConfig = getGridConfig(this);
    const permissions = pageContext.Permissions;

    this.isForVNext = isForVNext;
    this.scope = scope;
    this.router = router;
    this.filterService = filterService;
    this.isInitialGrid = true;
    this.permissions = permissions;
    this.rejectionPopupContainerSelector = '#rejectionContainer';
    this.popoverId = 'delivery-status-popup-callout';
    this.appealAggregator = null;

    const RequestExceptionCallBack =
    (assetsGrid, container = null) => {
      if (!this || !this.appealAggregator) {
        this.appealAggregator = new AppealAggregator({
          grid: assetsGrid.grid,
          controller: 'Asset',
          submit: 'SubmitAppeal',
          menuToFollow: 'button-ad-edit',
          isForVNext: this.isForVNext,
          permissions,
        });
      }
      this.appealAggregator.openBulkEditor(
        i18n.getString('bulkAssetAppealTitle'),
        container, null, false, null, this.scope
      );
      return this.appealAggregator.bulkEditor;
    };

    const RequestExceptionCallBackFromPopup = (assetsGrid) => {
      if (this.isForVNext) {
        this.trigger('closeDeliveryStatusPopup');
      } else {
        $(this.rejectionPopupContainerSelector).hide();
      }

      if (isForVNext) {
        const appealView = RequestExceptionCallBack(assetsGrid);
        assetsGrid.overrideWith(appealView.render());

        appealView.on('hide', () => {
          assetsGrid.overrideWith(null);
          appealView.remove();
        });

        return appealView;
      }

      return RequestExceptionCallBack(assetsGrid, '.assets-bulk-appeal', true);
    };

    this.RequestExceptionCallBack = RequestExceptionCallBack;
    this.RequestExceptionCallBackFromPopup = RequestExceptionCallBackFromPopup;

    this.grid = pgridFactory({
      gridConfig,
      plugins: getPlugins(this),
      preferenceConfig,
    });

    this.grid.gridView.on('willUpdate', (changes) => {
      // for the perf issue, should not patch preference when init view
      if (this.isInitialGrid) {
        this.isInitialGrid = false;
        return;
      }
      handleSortChange(changes, this.grid.gridView, gridConfig.name);
    });

    if (isForVNext) {
      this.filterService.on('filter-state-changed', () => {
        updateGridQuery(
          this.grid.gridView,
          this.filterService.getCombinedFilter(),
          true
        );
      });
    }

    if (permissions.dynamic && permissions.dynamic.AudienceAssetReport) {
      this.grid.gridView.on('didReload', (_isOk, data) => {
        const { config: { columns } } = this.grid;
        const patch = {};
        const videoItems = _.filter(data && data.items, item => _.get(item, 'AssociationType', '').includes('Video'));

        if (_.isEmpty(videoItems)) {
          patch.columns = columns.filter(col => !col.name.includes('Video'));
        } else {
          patch.columns = columns;
        }

        this.grid.gridView.patch(patch);
      });
    }

    return this.grid.gridView;
  }

  addEditorialHandler({ popupView }) {
    popupView.$('#requestException').on('click', _.partial(this.RequestExceptionCallBackFromPopup, this));
    registerCountryListEvent(popupView.$el);
  }

  showRejectionReason(event) {
    const key = this.grid.gridView.keyOfElement(event.target);
    const item = this.grid.gridView.itemWithKey(key);

    import(/* webpackMode: 'eager' */'@bingads-webui-campaign/delivery-status-popup-factory').then(({ showRejectionReasons }) => {
      showRejectionReasons({
        event,
        i18n,
        entity: item,
        deliveryStatusDetails: [item.EditorialStatus],
        getCustomTemplate: _.partial(getReasonTemplates, {
          event,
          scope: this.scope,
          i18n,
          item,
          grid: this.grid,
          permissions: this.permissions,
        }),
        useCustomTemplate: deliveryStatus => _.contains(['Rejected', 'RejectedAppealRejected', 'ApprovedLimited', 'ApprovedLimitedAppealRejected'], deliveryStatus),
        popoverId: this.popoverId,
        isForVNext: this.isForVNext,
        view: this.grid.gridView,
        afterRender: popup => this.addEditorialHandler({ popupView: popup }),
        isEditorialCategoryEnabled: true,
        isForAdsPageOnly: true,
      });
    });
  }

  hideRejectionPopup(e) {
    if ($(e.relatedTarget).closest(`#${this.popoverId}`).length === 0) {
      this.trigger('closeDeliveryStatusPopup');
    }
  }

  renderTrunk() {
    if (this.isForVNext) {
      this.filterService.refreshSource();
      this.filterService.initializeCombinedFilterPromise.then(() => {
        updateGridQuery(
          this.grid.gridView,
          this.filterService.getCombinedFilter(),
          true
        );
      });
    }
    super.renderTrunk();
  }

  get className() { return 'assets-grid'; }
}
